import React, { useState } from "react";
import { Mail, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function NewsletterForm() {
    const { t } = useLang();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            if (sp.get("newsletter") === "1") {
                setTimeout(() => {
                    const el = document.getElementById("newsletter");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    setTimeout(() => inputRef.current?.focus(), 700);
                }, 400);
            }
        } catch { /* ignore */ }
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            await api.post("/newsletter/subscribe", { email: email.trim().toLowerCase() });
            setDone(true);
            setEmail("");
            toast.success(t("newsletter.success"));
        } catch (err) {
            toast.error(err.response?.data?.detail || t("newsletter.error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section data-testid="newsletter-section" className="relative py-20 md:py-24 border-t border-white/10 bg-zinc-950">
            <div className="max-w-[1100px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div>
                    <p className="text-[10px] tracking-[0.4em] uppercase text-abnormal-acid font-bold mb-4 inline-flex items-center gap-2">
                        <Mail size={14} /> {t("newsletter.eyebrow")}
                    </p>
                    <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95]">
                        {t("newsletter.title1")} <span className="text-abnormal-acid">{t("newsletter.title2")}</span>
                    </h2>
                    <p className="text-zinc-400 mt-5 leading-relaxed max-w-md">{t("newsletter.sub")}</p>
                </div>

                {done ? (
                    <div data-testid="newsletter-success" className="border border-abnormal-acid/40 bg-abnormal-acid/5 p-8 flex flex-col items-start gap-3">
                        <Check className="text-abnormal-acid" size={28} />
                        <p className="font-display text-3xl uppercase leading-none">{t("newsletter.thanks")}</p>
                        <p className="text-sm text-zinc-300">{t("newsletter.thanksSub")}</p>
                    </div>
                ) : (
                    <form onSubmit={submit} data-testid="newsletter-form" className="flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                data-testid="newsletter-email"
                                ref={inputRef}
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t("newsletter.placeholder")}
                                className="flex-1 bg-black border border-white/10 focus:border-abnormal-acid outline-none px-4 py-3 text-sm"
                            />
                            <button
                                data-testid="newsletter-submit"
                                type="submit"
                                disabled={busy || !email}
                                className="bg-abnormal-acid text-black uppercase tracking-wider text-xs font-bold px-5 py-3 hover:bg-abnormal-paper hover:text-black transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                            >
                                <Send size={14} /> {busy ? t("newsletter.busy") : t("newsletter.cta")}
                            </button>
                        </div>
                        <p className="text-[11px] text-zinc-600">{t("newsletter.consent")}</p>
                    </form>
                )}
            </div>
        </section>
    );
}
